import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import SubTaskContainerHeader from '../SubTaskContainerHeader';
import pageNames from './pageNames';
import {
  trackSubtaskStepView,
  trackClaimTypeSelection,
  trackSubtaskValidationError,
} from '../../utils/tracking/features/subtaskTracking';

const dataKey = 'view:claimType';

const content = {
  label: 'What kind of claim do you want to file?',
  errorMessage: 'Please select a type of claim',
};

const options = [
  {
    value: pageNames.fileClaim,
    label:
      'I’m filing a claim for a new condition, a condition that’s gotten worse, or both',
  },
  {
    value: pageNames.disagreeFileClaim,
    label: 'I disagree with a VA decision on a previously submitted claim.',
  },
];

const optionValues = options.map(option => option.value);

const validate = data => optionValues.includes(data[dataKey]);

const getNextPage = data => (validate(data) ? data[dataKey] : '');

/**
 * Appeals page
 * @param {Object} data - subtask data
 * @param {Boolean} error - page submitted & error state
 * @param {Function} setPageData - updates subtask data
 * @returns {JSX}
 */
const AppealsPage = ({ data = {}, error, setPageData }) => {
  const { addStep, getHistory } = usePathHistory();

  // ──────────────────────────────────────────────
  // STEP VIEW TRACKING
  // ──────────────────────────────────────────────
  useEffect(() => {
    trackSubtaskStepView({
      stepName: pageNames.appeals,
      pathHistoryContext: { addStep, getHistory },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ──────────────────────────────────────────────
  // ERROR TRACKING
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (error) {
      trackSubtaskValidationError(
        pageNames.appeals,
        'claim-type',
        content.errorMessage,
      );
    }
  }, [error]);

  const handlers = {
    setAppealChoice: ({ target }) => {
      const { value } = target;
      setPageData({ [dataKey]: value || null });
    },
  };

  return (
    <>
      <SubTaskContainerHeader />
      <VaRadio
        label={content.label}
        labelHeaderLevel="2"
        error={error ? content.errorMessage : null}
        onRadioOptionSelected={handlers.setAppealChoice}
        className="vads-u-margin-top--0"
        required
      >
        {options.map(({ value, label }) => (
          <VaRadioOption
            key={value}
            className="vads-u-margin-y--2"
            name="appealChoice"
            id={value}
            value={value}
            label={label}
            checked={value === data[dataKey]}
          />
        ))}
      </VaRadio>
    </>
  );
};

AppealsPage.propTypes = {
  data: PropTypes.shape({}),
  error: PropTypes.bool,
  setPageData: PropTypes.func,
};

export default {
  name: pageNames.appeals,
  component: AppealsPage,
  validate,
  back: pageNames.start,
  next: getNextPage,
  // ──────────────────────────────────────────────
  // USER INPUT TRACKING
  // ──────────────────────────────────────────────
  onContinue: data => {
    const claimType =
      data[dataKey] === pageNames.fileClaim ? 'new-or-worsened' : 'disagree';
    trackClaimTypeSelection(claimType);
  },
};
